import axios from 'axios';
import { RestApiPaginationResult } from 'src/components/AuditLogs/service';
import {
  AppStoreAppConfiguration,
  AppStoreAppSummary,
  CreateAppStoreApp,
  ExecuteConfigurationActionRequest,
  RestApiResultAppStoreApp,
  UpdateAppStoreApp,
} from 'src/overrides/@flipdish/api-client-typescript/api';

import { getApiEndPoint } from '../../../helpers/apibase';
import { mapServerError } from '../../../services/utils/serverErrorMapper';

const baseURL = getApiEndPoint();
const appStoreApi = axios.create({
  baseURL: `${baseURL}/api/v1.0`,
  withCredentials: true,
});

const getMyAppStoreAppsKey = 'getMyAppStoreApps';
async function getMyAppStoreApps(appId: string) {
  try {
    const result = await appStoreApi.get(`${appId}/appstore/apps/`);
    return result.data;
  } catch (e) {
    throw mapServerError(e);
  }
}

const getAppStoreAppsKey = 'getApps';
async function getAppStoreApps(
  query: string,
  page: number,
  limit: number
): Promise<RestApiPaginationResult<AppStoreAppSummary[]>> {
  try {
    const result = await appStoreApi.get(
      `appstore/apps?search=${query.trim()}&page=${page}&limit=${limit}`
    );
    return result.data;
  } catch (e) {
    throw mapServerError(e);
  }
}

const getLightSpeedOAuthResponseStatusKey = 'getOAuthResponseStatus';
async function getLightSpeedOAuthResponseStatus(
  appStoreAppId: string,
  state: string | null,
  code: string | null
) {
  try {
    const result = await appStoreApi.get(`appstore/oauthresponse/${appStoreAppId}/responsecode`, {
      params: { state, code },
    });
    return result.data;
  } catch (e) {
    throw mapServerError(e);
  }
}

const getDeveloperAppStoreAppsKey = 'getAppsByDeveloper';
async function getDeveloperAppStoreApps(query: string, page: number, limit: number) {
  try {
    const result = await appStoreApi.get(
      `appstore/apps?search=${query.trim()}&page=${page}&limit=${limit}&excludeNotOwned=true`
    );
    return result.data;
  } catch (e) {
    throw mapServerError(e);
  }
}

const getAppStoreAppByIdKey = 'getAppById';
async function getAppStoreAppById(appStoreAppId: string) {
  try {
    const result = await appStoreApi.get<RestApiResultAppStoreApp>(
      `appstore/apps/${appStoreAppId}`
    );
    return result.data;
  } catch (e) {
    throw mapServerError(e);
  }
}

const getAppStoreAppConfigsByIdKey = 'getAppById';
async function getAppStoreAppConfigsById(appId: string, appStoreAppId: string) {
  try {
    const result = await appStoreApi.get(`${appId}/appstore/apps/${appStoreAppId}`);
    return result.data;
  } catch (e) {
    throw mapServerError(e);
  }
}

const getAppStoreAppConfigsWithSubscriptionsByIdKey = 'getAppStoreAppConfigsWithSubscriptionsById';
async function getAppStoreAppConfigsWithSubscriptionsById(appId: string, appStoreAppId: string) {
  try {
    const result = await appStoreApi.get(`${appId}/appstore/apps_subscriptions/${appStoreAppId}`);
    return result.data;
  } catch (e) {
    throw mapServerError(e);
  }
}

const createAppStoreAppConfigKey = 'getAppStoreAppConfig';
async function createAppStoreAppConfig(appId: string, appStoreAppId: string) {
  try {
    const result = await appStoreApi.post(`${appId}/appstore/apps/${appStoreAppId}/config`);
    return result.data;
  } catch (e) {
    throw mapServerError(e);
  }
}

const getAppStoreAppConfigByIdKey = 'getAppById';
async function getAppStoreAppConfigById(appId: string, appStoreAppId: string, configId: string) {
  try {
    const result = await appStoreApi.get(
      `${appId}/appstore/apps/${appStoreAppId}/config/${configId}`
    );
    return result.data;
  } catch (e) {
    throw mapServerError(e);
  }
}

const getAppStoreAppConfigsKey = 'getAppStoreAppConfigs';
async function getAppStoreAppConfigs(appId: string, appStoreAppId: string) {
  try {
    const result = await appStoreApi.get(`${appId}/appstore/apps/${appStoreAppId}/config`);
    return result.data;
  } catch (e) {
    throw mapServerError(e);
  }
}

async function getAppStoreAppExternalFunctionSigningKey(appStoreAppId: string, oauthAppId: string) {
  try {
    const result = await appStoreApi.get(
      `oauthclients/${oauthAppId}/appstore/apps/${appStoreAppId}/external_function_signing_key`
    );
    return result.data;
  } catch (e) {
    throw mapServerError(e);
  }
}

async function updateAppStoreAppConfig(
  appId: string,
  appStoreAppId: string,
  configId: string,
  config: any
) {
  try {
    const result = await appStoreApi.put(
      `${appId}/appstore/apps/${appStoreAppId}/config/${configId}`,
      config
    );
    return result.data;
  } catch (e) {
    throw mapServerError(e);
  }
}

async function deleteAppStoreConfig(appId: string, appStoreAppId: string, configId: string) {
  try {
    const result = await appStoreApi.delete(
      `${appId}/appstore/apps/${appStoreAppId}/config/${configId}`
    );
    return result.data;
  } catch (e) {
    throw mapServerError(e);
  }
}

async function createAppStoreApp(config: CreateAppStoreApp, oauthAppId: string) {
  try {
    const result = await appStoreApi.post(`oauthclients/${oauthAppId}/appstore/apps`, config);
    return result.data;
  } catch (e) {
    throw mapServerError(e);
  }
}

async function executeConfigurationAction(
  appId: string,
  appStoreAppId: string,
  configId: string,
  body: ExecuteConfigurationActionRequest
) {
  try {
    const result = await appStoreApi.post(
      `${appId}/appstore/apps/${appStoreAppId}/config/${configId}/action`,
      body
    );
    return result.data;
  } catch (e) {
    throw mapServerError(e);
  }
}

async function updateAppStoreApp(
  appStoreAppId: string,
  config: UpdateAppStoreApp,
  oauthAppId: string
) {
  try {
    const result = await appStoreApi.put(
      `oauthclients/${oauthAppId}/appstore/apps/${appStoreAppId}`,
      config
    );
    return result.data;
  } catch (e) {
    throw mapServerError(e);
  }
}

async function updateAppStoreAppVerification(
  appStoreAppId: string,
  verificationStatus: AppStoreAppConfiguration.VerificationStatusEnum,
  oauthAppId: string
) {
  try {
    const result = await appStoreApi.put(
      `oauthclients/${oauthAppId}/appstore/apps/${appStoreAppId}/verification?verificationStatus=${verificationStatus}`
    );
    return result.data;
  } catch (e) {
    throw mapServerError(e);
  }
}

async function addAppStoreAppLogo(appStoreAppId: string, image: File, oauthAppId: string) {
  try {
    const formData = new FormData();
    formData.append('file', image);
    const result = await appStoreApi.post(
      `oauthclients/${oauthAppId}/appstore/apps/${appStoreAppId}/logo`,
      formData
    );
    return result;
  } catch (e) {
    throw mapServerError(e);
  }
}

async function deleteAppStoreApp(appStoreAppId: string, oauthAppId: string) {
  try {
    const result = await appStoreApi.delete(
      `oauthclients/${oauthAppId}/appstore/apps/${appStoreAppId}`
    );
    return result.data;
  } catch (e) {
    throw mapServerError(e);
  }
}

export const appStoreService = {
  executeConfigurationAction,
  getMyAppStoreAppsKey,
  getMyAppStoreApps,
  getAppStoreApps,
  getAppStoreAppsKey,
  getAppStoreAppConfigsWithSubscriptionsById,
  getAppStoreAppConfigsWithSubscriptionsByIdKey,
  getAppStoreAppById,
  getAppStoreAppByIdKey,
  createAppStoreAppConfigKey,
  createAppStoreAppConfig,
  getAppStoreAppConfigByIdKey,
  getAppStoreAppConfigById,
  getAppStoreAppConfigsKey,
  getAppStoreAppConfigsByIdKey,
  getAppStoreAppConfigsById,
  getAppStoreAppExternalFunctionSigningKey,
  updateAppStoreAppConfig,
  getDeveloperAppStoreApps,
  getDeveloperAppStoreAppsKey,
  createAppStoreApp,
  updateAppStoreApp,
  addAppStoreAppLogo,
  updateAppStoreAppVerification,
  deleteAppStoreApp,
  deleteAppStoreConfig,

  getLightSpeedOAuthResponseStatusKey,
  getLightSpeedOAuthResponseStatus,
};
